import React, { useCallback, useEffect, useState } from 'react';
import Navbar from '../components/Navbar';
import { Box, Button, CircularProgress, FormControl, Grid, IconButton, MenuItem, Modal, Pagination, TextField, Typography } from '@mui/material';
import CallCard from '../components/CallCard ';
import api from "../api";
import { Close, Phone } from '@mui/icons-material';
import Loader from "../components/Loader";
import { toast } from 'sonner';
import NoDataFound from "../components/NoDataFound"
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { parsePhoneNumberFromString, isPossiblePhoneNumber } from "libphonenumber-js";
import { ButtonSx, paginationSx, textFieldSx, phoneNumberStyleSx } from '../styles/GlobalStyles';
import { useParams } from 'react-router-dom';
import { useGetAllPermissions } from './utils/getAllPermissions';

const PhoneNumber = () => {
    const [numbers, setAllNumbers] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [debounceTimeout, setDebounceTimeout] = useState(null);
    const [searchValue, setSearchValue] = useState("");
    const [searchOption, setSearchOption] = useState('All');
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [initialLoad, setInitialLoad] = useState(true);
    const token = JSON.parse(localStorage.getItem("token")).token;
    const OrganizationId = localStorage.getItem("Organization");
    const userRole = localStorage.getItem("Role") || "Admin"; // Default to Admin if not found
    const [phoneProviders, setPhoneProviders] = useState([]);
    const [formValues, setFormValues] = useState({
        PhoneNumber: '',
        ApiCredentials: '',
        Organization: '',
        Provider: '',
        Label: ''
    });
    const [error, setError] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { company_id } = useParams(); // Will be undefined for regular users
    const { permissions: adminPermissions, permissionsLoading: adminPermissionsLoading, getAllPermissions } = useGetAllPermissions();
    // Use the hook results only if user is Admin
    const permissions = userRole === "Admin" ? adminPermissions : null;
    const permissionsLoading = userRole === "Admin" ? adminPermissionsLoading : false;

    const handleSearchChange = (event) => {
        const newSearchValue = event.target.value;
        setSearchValue(newSearchValue);
        if (debounceTimeout) {
            clearTimeout(debounceTimeout);
        }
        
        const timeout = setTimeout(() => {
            // Reset to page 1 when searching
            setCurrentPage(1);
            getAllNumbers({ search: newSearchValue, page: 1, showFullPageLoading: false });
        }, 500);
        
        setDebounceTimeout(timeout);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormValues({ ...formValues, [name]: value });
    };
    
    const handlePhoneNumberInputChange = (value, country) => {
        if (!country || !country.name || !country.countryCode) {
            setError("Invalid country information");
            return;
        }

        if (!value) {
            setError(`Please enter a phone number for ${country.name}.`);
            return;
        }
        
        try {
            const phoneNumber = parsePhoneNumberFromString(`+${value}`, country.countryCode.toUpperCase());
            const formattedNumber = `+${value}`;
            const countryCode = country.countryCode.toUpperCase();
            
            // Basic validation for all users
            let isValid = phoneNumber && phoneNumber.isValid();
            
            // Extra strict validation for Admin users only
            if (userRole === "Admin") {
                isValid = isValid && isPossiblePhoneNumber(formattedNumber, countryCode);
            }
            
            if (!isValid) {
                setError(`Invalid phone number format for ${country.name}.`);
            } else {
                setError("");
            }
        } catch (error) {
            setError(`Invalid input. Please check the phone number for ${country.name}.`);
        }

        setFormValues({ ...formValues, PhoneNumber: value });
    };
    
    const handleOptionChange = (event) => {
        setSearchOption(event.target.value);
        
        if (searchValue) {
            if (debounceTimeout) {
                clearTimeout(debounceTimeout);
            }
            
            const timeout = setTimeout(() => {
                // Reset to page 1 when changing search option
                setCurrentPage(1);
                getAllNumbers({ search: searchValue, page: 1, showFullPageLoading: false, searchField: event.target.value });
            }, 500);
            
            setDebounceTimeout(timeout);
        }
    };

    const getAllPhoneProviders = useCallback(async () => {
        try {
            const response = await api.get("/providers",
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

            setPhoneProviders(response.data?.data?.providers);
        } catch (error) {
            console.error(error);
        }
    }, [token]);
    console.log(OrganizationId, "OrganizationId");

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formValues.OrganizationId) {
            formValues.Organization = OrganizationId;
        }
        setIsSubmitting(true);
        try {
            const response = await api.post("/phones/create-phone", formValues, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            toast.success(response.data?.message || "Phone created successfully");
            setOpen(false);
            getAllNumbers({ showFullPageLoading: true }); // Use full page loading for initial loads
            
            // Clear form values after successful submission
            setFormValues({
                PhoneNumber: '',
                ApiCredentials: '',
                Organization: '',
                Provider: '',
                Label: ''
            });
            
            setError("");
            
        } catch (error) {
            toast.error(error.response?.data?.message || "Error creating phone");
            console.error(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const searchOptions = [
        { value: 'Name', label: 'Name' },
        { value: 'Phone', label: 'Phone Number' },
    ];

    const getAllNumbers = useCallback(async (options = {}) => {
        const {
            search = "",
            page = currentPage,
            limit = 9,
            showFullPageLoading = true,
            searchField = searchOption
        } = options;

        if (showFullPageLoading) {
            setInitialLoad(true);
        }
        setLoading(true);
        try {
            // Build the base URL with common query parameters
            let url = `/phones?limit=${limit}&search=${search}&page=${page}&searchField=${searchField}`;
            // Append organization ID if available
            if (OrganizationId) {
                url += `&organizationId=${OrganizationId}`;
            }
            
            const response = await api.get(url, {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            });
                
            setAllNumbers(response.data.data.phones);
            setTotalPages(response.data.data.totalPages);
            
        } catch (error) {
            console.error("Error fetching phone numbers:", error);
            setInitialLoad(false);
        } finally {
            setLoading(false);
            setInitialLoad(false);
        }
    }, [token, searchOption, OrganizationId, currentPage]);

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
        getAllNumbers({ search: searchValue, page: value, showFullPageLoading: false });
    };

    useEffect(() => {
        if (token) {
            if (userRole === "Admin") {
                getAllPermissions();
            }
            getAllNumbers({ search: searchValue, page: currentPage, showFullPageLoading: true });
            getAllPhoneProviders();
        }
    }, [token, getAllNumbers, getAllPhoneProviders, userRole]);
    
    if ((loading && initialLoad) || (userRole === "Admin" && permissionsLoading)) {
            return <Loader />
        }

    // Check permissions for Admin users
    const canViewPhones = userRole === "Admin" ? permissions?.includes("fetch_phone") : true;
    const canCreatePhones = userRole === "Admin" ? permissions?.includes("create_phone") : true;

    return (
        <Box>
            {/* Navbar - keep search only for regular users */}
            <Navbar
                title="Phone Numbers"
                searchOptions={searchOptions}
                searchOption={searchOption}
                searchValue={searchValue}
                onSearchOptionChange={handleOptionChange}
                onSearchValueChange={handleSearchChange}
            />

            {/* Main content area */}
            <Box px={3} py={2} bgcolor="#F9FAFB">
                {/* Phone Numbers Section */}
                <Box
                    sx={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #E5E7EB',
                    borderRadius: '1rem',
                    p: 3,
                    mt: 3
                    }}
                >
                    <Box mb={4} display="flex" justifyContent="space-between" alignItems="center">
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem', color: '#111827' }}>
                            Phone Number
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.5 }}>
                            See all assistants linked to phone numbers.
                            </Typography>
                        </Box>
                        
                        {/* Button to add new phone number - conditionally render based on permissions */}
                        {canCreatePhones && (
                            <Button 
                                variant="contained" 
                                sx={ButtonSx} 
                                onClick={() => setOpen(true)} 
                                startIcon={<Phone />}
                            >
                                Add Phone Number
                            </Button>
                        )}
                    </Box>

                    <Grid container spacing={2}>
                        {canViewPhones ? (
                            numbers && numbers.length > 0 ? (
                                numbers.map((number) => (
                                    <Grid item xs={12} sm={6} md={4} lg={4} key={number._id || number.id}>
                                        <CallCard 
                                            data={number} 
                                            url={userRole === "Admin" ? 
                                                `/company/${company_id}/phone-number/${number._id}` : 
                                                `/phone-number/${number._id}`
                                            } 
                                        />
                                    </Grid>
                                ))
                            ) : (
                                <Box sx={{ display: 'flex', justifyContent: 'center', width: "100%", height: "70dvh" }}>
                                    <NoDataFound
                                        title='No Phone Number Found'
                                        subtitle={userRole === "Admin" ? 
                                            "No phone numbers assigned to this company." : 
                                            "There is no Phone Number found to show the data right now."
                                        }
                                        buttonLabel=""
                                        buttonLink=""
                                    />
                                </Box>
                            )
                        ) : (
                            <Box sx={{ display: 'flex', justifyContent: 'center', width: "100%", height: "70dvh" }}>
                                <NoDataFound
                                    title="Phone Numbers"
                                    subtitle="You do not have permission to view phone numbers."
                                />
                            </Box>
                        )}
                    </Grid>

                    {numbers && numbers.length > 0 &&
                        <Pagination
                            count={totalPages}
                            page={currentPage}
                            onChange={handlePageChange}
                            sx={paginationSx}
                        />
                    }
                </Box> 
            </Box>

            {/* Modal for adding new phone number - common UI with conditional styling */}
            <Modal open={open} onClose={() => setOpen(false)} >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 480,
                    bgcolor: 'background.paper',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                    borderRadius: '16px',
                    p: 4,
                    outline: 'none',
                }}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Typography id="add-phone-number-modal" variant="h6" component="h6" sx={{ fontWeight: 700, fontSize: '1.25rem' }}>
                            Add Phone Number
                        </Typography>
                        <IconButton onClick={() => setOpen(false)} size="small" sx={{ p: 0 }}>
                            <Close fontSize="small" />
                        </IconButton>
                    </Box>
                    
                    {/* Form for adding new phone number */}
                    <form onSubmit={handleSubmit} style={{ width: "100%", display: "flex", flexDirection: "column", gap: "1rem" }}>
                        {/* Phone Number Provider Selection */}
                        <FormControl fullWidth size="small">
                            <TextField
                                select
                                name="Provider"
                                value={formValues.Provider}
                                onChange={handleInputChange}
                                sx={textFieldSx}
                                SelectProps={{
                                    displayEmpty: true,
                                }}
                                inputProps={{
                                    'aria-label': 'Select Category',
                                }}
                                required
                            >
                                <MenuItem value="">
                                    <span style={{ color: '#9e9e9e' }}>
                                    Select the Phone Number Provider*
                                    </span>
                                </MenuItem>
                                {phoneProviders?.map((provider, index) => (
                                    <MenuItem key={index} value={provider._id} style={{ textAlign: "center" }}>
                                        {provider.ProviderName}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </FormControl>

                        {/* Label Input */}
                        <TextField
                            placeholder='Label*'
                            name="Label"
                            value={formValues.Label}
                            onChange={handleInputChange}
                            fullWidth
                            required
                            sx={textFieldSx}
                        />
                        
                        <Box sx={{
                            display:'flex',
                            flexDirection:'column',
                            gap:0.5,
                        }}>
                            <PhoneInput
                                country={"us"}
                                value={formValues.PhoneNumber}
                                onChange={(value, country) => handlePhoneNumberInputChange(value, country)}
                                inputStyle={phoneNumberStyleSx.inputStyle}
                                buttonStyle={phoneNumberStyleSx.buttonStyle}
                                containerStyle={phoneNumberStyleSx.containerStyle}
                                inputProps={phoneNumberStyleSx.inputProps(error)}
                            />
                            {error && (
                                <p style={{ color: "red", marginTop: "0.5rem", fontSize: "14px" }}>
                                    {error}
                                </p>
                            )}
                        </Box>
                        
                        {/* API Credentials Input */}
                        <TextField
                            placeholder="API Credentials*"
                            name="ApiCredentials"
                            value={formValues.ApiCredentials}
                            onChange={handleInputChange}
                            required
                            sx={textFieldSx}
                            multiline
                            rows={5}
                        />
                        
                        <Button
                            type="submit"
                            variant="contained"
                            sx={ButtonSx}
                            disabled={error || !formValues.PhoneNumber || isSubmitting} 
                        >
                            {isSubmitting ? (
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <CircularProgress size={20} sx={{ mr: 1, color:"#02042d" }} />
                                    Submitting...
                                </Box>
                            ) : (
                                'Submit'
                            )}
                        </Button>
                    </form>
                </Box>
            </Modal>
        </Box>
    );
}

export default PhoneNumber;
